import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { apiRequest } from '../../lib/api';
import { ApiError } from '../../lib/http';
import { ShopperApi, shopperApi } from '../../lib/shopperApi';
import { useAuthStore } from '../../store/authStore';
import { useWishlistStore } from '../../store/wishlistStore';
import { ProductDetails, WishlistEntry } from '../../types/api';
import { ProductCards } from '../ProductGrid/ProductGrid';
import NotifyMeForm from '../NotifyMe/NotifyMeForm';
import './Wishlist.css';

type Load =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | {
      readonly status: 'ready';
      readonly entries: readonly WishlistEntry[];
    };

/** A guest's hearts live only as ids on this device; fetch a card for each one still for sale. */
async function guestEntries(ids: readonly string[]): Promise<WishlistEntry[]> {
  const results = await Promise.allSettled(
    ids.map((id) => apiRequest<ProductDetails>(`/items/${encodeURIComponent(id)}`)),
  );
  const failed = results.find(
    (r): r is PromiseRejectedResult =>
      r.status === 'rejected' && !(r.reason instanceof ApiError && [404, 422].includes(r.reason.status)),
  );
  if (failed) throw failed.reason;
  return results.flatMap((r) =>
    r.status === 'fulfilled'
      ? [{ ...r.value, price: Number(r.value.price), in_stock: r.value.in_stock ?? r.value.quantity !== 0 }]
      : [],
  );
}

interface Props {
  readonly api?: ShopperApi;
}

export default function WishlistPage({ api = shopperApi }: Props) {
  useDocumentTitle('Wishlist');
  const auth = useAuthStore((s) => s.status);
  const ids = useWishlistStore((s) => s.ids);
  const entries = useWishlistStore((s) => s.entries);
  const synced = useWishlistStore((s) => s.synced);
  const hydrate = useWishlistStore((s) => s.hydrate);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const idsKey = ids.join(',');

  useEffect(() => {
    if (auth === 'unknown') return;
    let current = true;
    const request =
      auth === 'authed'
        ? api.wishlist().then((fresh) => {
            hydrate(fresh);
            return fresh;
          })
        : guestEntries(idsKey ? idsKey.split(',') : []);
    request.then(
      (fresh) => current && setLoad({ status: 'ready', entries: fresh }),
      (error) =>
        current &&
        setLoad({
          status: 'error',
          message: error instanceof ApiError ? error.message : 'Could not load your wishlist.',
        }),
    );
    return () => {
      current = false;
    };
    // Guests reload when hearts change; the account list is refreshed once per visit.
  }, [api, auth, hydrate, attempt, idsKey]);

  // Removing a heart here hides the card at once, without waiting for a reload.
  const shown = (load.status === 'ready' ? (synced ? entries : load.entries) : []).filter((e) =>
    ids.includes(e.item_id),
  );
  const available = shown.filter((e) => e.in_stock !== false);
  const soldOut = shown.filter((e) => e.in_stock === false);

  return (
    <section className="wishlist-page page-container">
      <h1>Wishlist</h1>
      {auth === 'guest' && (
        <p className="wishlist-page__note">
          Saved on this device only. <Link to="/login?next=/wishlist">Sign in</Link> to keep your wishlist on
          every device.
        </p>
      )}
      {load.status === 'loading' || auth === 'unknown' ? (
        <p role="status">Loading your wishlist…</p>
      ) : load.status === 'error' ? (
        <div className="wishlist-page__error" role="alert">
          <p>{load.message}</p>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      ) : shown.length === 0 ? (
        <div className="wishlist-page__empty">
          <p>Tap the heart on any piece to save it here.</p>
          <Link to="/">Browse the collection</Link>
        </div>
      ) : (
        <>
          {available.length > 0 && (
            <section aria-labelledby="wishlist-available">
              <h2 id="wishlist-available">Available now</h2>
              <ProductCards items={available} />
            </section>
          )}
          {soldOut.length > 0 && (
            <section aria-labelledby="wishlist-sold-out">
              <h2 id="wishlist-sold-out">Sold out</h2>
              {auth === 'authed' ? (
                <p className="wishlist-page__note">We&apos;ll email you when these are back in stock.</p>
              ) : (
                <div className="wishlist-page__notify">
                  <NotifyMeForm
                    itemIds={soldOut.map((e) => e.item_id)}
                    intro={`We'll email you once when ${
                      soldOut.length === 1 ? 'this piece is' : `any of these ${soldOut.length} pieces are`
                    } back in stock.`}
                  />
                </div>
              )}
              <ProductCards items={soldOut} />
            </section>
          )}
        </>
      )}
    </section>
  );
}
